import { CheckIcon } from "lucide-react";
import { ReactNode, RefObject, useEffect } from "react";

import LoadingSpinner from "@/components/LoadingSpinner";
import { cn } from "@/lib/utils";

interface SelectableOptionListProps<T> {
  isLoading: boolean;
  loadingMessage?: string;
  emptyStateTitle: string;
  emptyStateDescription: string;
  items: T[];
  selectedKey: string | number | null;
  readonly: boolean;
  getItemKey: (item: T) => string | number;
  getItemLeading?: (item: T) => ReactNode;
  getItemLabel: (item: T) => ReactNode;
  getItemDescription?: (item: T) => ReactNode;
  isItemDisabled?: (item: T) => boolean;
  onItemSelect: (item: T) => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  infiniteScrollRef?: RefObject<HTMLDivElement>;
}

export function SelectableOptionList<T>({
  isLoading,
  loadingMessage = "Loading...",
  emptyStateTitle,
  emptyStateDescription,
  items,
  selectedKey,
  readonly,
  getItemKey,
  getItemLeading,
  getItemLabel,
  getItemDescription,
  isItemDisabled,
  onItemSelect,
  hasNextPage,
  isFetchingNextPage,
  infiniteScrollRef,
}: SelectableOptionListProps<T>) {
  useEffect(() => {
    if (isLoading || readonly || selectedKey !== null) {
      return;
    }

    const firstSelectableIndex = items.findIndex(item => !isItemDisabled?.(item));
    if (firstSelectableIndex >= 0) {
      onItemSelect(items[firstSelectableIndex]);
    }
  }, [isItemDisabled, isLoading, items, onItemSelect, readonly, selectedKey]);

  if (isLoading) {
    return (
      <div className="flex h-full min-h-64 items-center justify-center px-6 py-10">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <LoadingSpinner size={4} />
          <span>{loadingMessage}</span>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex h-full min-h-64 items-center justify-center px-6 py-10">
        <div className="max-w-sm text-center">
          <div className="text-sm font-medium text-gray-900">{emptyStateTitle}</div>
          <div className="mt-1.5 text-sm leading-5 text-gray-500">{emptyStateDescription}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-6 py-4">
      <ul className="space-y-1">
        {items.map(item => {
          const itemKey = getItemKey(item);
          const isSelected = selectedKey !== null && itemKey === selectedKey;
          const isDisabled = readonly || (isItemDisabled && isItemDisabled(item));
          const itemDescription = getItemDescription?.(item);
          return (
            <li key={itemKey}>
              <button
                type="button"
                onClick={() => onItemSelect(item)}
                disabled={isDisabled}
                aria-pressed={isSelected}
                className={cn(
                  "group flex min-h-14 w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm",
                  "transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-300",
                  isSelected && "bg-gray-100 hover:bg-gray-100",
                  isDisabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
                )}
              >
                {getItemLeading && (
                  <div
                    className={cn(
                      "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500 transition-colors",
                      isSelected && "bg-white text-gray-700 ring-1 ring-inset ring-gray-200",
                    )}
                  >
                    {getItemLeading(item)}
                  </div>
                )}
                <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-0.5 overflow-hidden">
                  <div className="flex w-full min-w-0 items-center gap-2 overflow-hidden">
                    {getItemLabel(item)}
                  </div>
                  {itemDescription && (
                    <div className="w-full truncate text-left text-xs leading-4 text-gray-500">
                      {itemDescription}
                    </div>
                  )}
                </div>
                {isSelected && (
                  <CheckIcon className="h-4 w-4 flex-shrink-0 text-gray-700" aria-hidden />
                )}
              </button>
            </li>
          );
        })}
        {hasNextPage && (
          <li>
            <div ref={infiniteScrollRef} className="flex justify-center py-2">
              {isFetchingNextPage && <LoadingSpinner size={5} />}
            </div>
          </li>
        )}
      </ul>
    </div>
  );
}
